/**
 * History Manager - Undo/Redo system for CAD operations
 *
 * Tracks all CAD operations and allows undoing/redoing them.
 * Stores state snapshots for each operation to enable reliable undo/redo.
 *
 * Design Principles:
 * - Command Pattern: Each operation is a command with execute/undo methods
 * - State Snapshots: Store full state before each operation for reliable undo
 * - Memory Efficient: Limit history size and clean up old snapshots
 * - Integration: Works seamlessly with existing CADViewer and CADDocument
 */

class HistoryManager {
  constructor(cadViewer, options = {}) {
    this.cadViewer = cadViewer;

    // History stacks
    this.undoStack = [];  // Array of operations that can be undone
    this.redoStack = [];  // Array of operations that can be redone

    // Configuration
    this.maxHistorySize = options.maxHistorySize || 50;  // Max operations to track
    this.enabled = options.enabled !== false;  // History tracking enabled/disabled

    // State
    this.isExecuting = false;  // Prevent recursive history tracking during undo/redo
    this.grouping = null;  // For grouping multiple operations into one undo step

    // Statistics
    this.stats = {
      totalOperations: 0,
      undoCount: 0,
      redoCount: 0
    };

    console.log('[HistoryManager] Initialized (max history:', this.maxHistorySize, ')');
  }

  /**
   * Record a new operation in history
   * @param {Object} operation - Operation object with type, execute, undo methods
   */
  recordOperation(operation) {
    if (!this.enabled || this.isExecuting) {
      return;
    }

    // Validate operation
    if (!operation || !operation.type) {
      console.warn('[HistoryManager] Invalid operation:', operation);
      return;
    }

    // Add timestamp and ID
    operation.timestamp = Date.now();
    operation.id = Utils.generateId();

    // If grouping is active, add to group
    if (this.grouping) {
      this.grouping.operations.push(operation);
      return;
    }

    // Clear redo stack when new operation is recorded
    this.redoStack = [];

    // Add to undo stack
    this.undoStack.push(operation);

    // Trim history if exceeds max size
    if (this.undoStack.length > this.maxHistorySize) {
      const removed = this.undoStack.shift();
      this._cleanupOperation(removed);
    }

    this.stats.totalOperations++;

    // Update UI buttons
    this._updateButtons();

    console.log('[HistoryManager] Recorded:', operation.type, '- Undo stack size:', this.undoStack.length);
  }

  /**
   * Undo the last operation
   * @returns {boolean} True if undo was successful
   */
  undo() {
    if (!this.canUndo()) {
      Utils.notify('Nothing to undo', 'info');
      return false;
    }

    const operation = this.undoStack.pop();

    try {
      this.isExecuting = true;

      // Execute undo
      if (operation.operations) {
        // Grouped operation - undo all in reverse order
        for (let i = operation.operations.length - 1; i >= 0; i--) {
          this._undoSingleOperation(operation.operations[i]);
        }
      } else {
        // Single operation
        this._undoSingleOperation(operation);
      }

      // Move to redo stack
      this.redoStack.push(operation);

      this.stats.undoCount++;

      // Update UI
      this._updateButtons();
      this._refreshViewer();

      // Show notification
      const opName = this._getOperationDisplayName(operation);
      Utils.notify(`Undid: ${opName}`, 'success');

      console.log('[HistoryManager] Undone:', operation.type);
      return true;

    } catch (error) {
      console.error('[HistoryManager] Undo error:', error);
      Utils.notify(`Undo failed: ${error.message}`, 'error');

      // Put operation back on undo stack
      this.undoStack.push(operation);
      return false;

    } finally {
      this.isExecuting = false;
    }
  }

  /**
   * Redo the last undone operation
   * @returns {boolean} True if redo was successful
   */
  redo() {
    if (!this.canRedo()) {
      Utils.notify('Nothing to redo', 'info');
      return false;
    }

    const operation = this.redoStack.pop();

    try {
      this.isExecuting = true;

      // Execute redo
      if (operation.operations) {
        // Grouped operation - redo all in order
        for (const op of operation.operations) {
          this._redoSingleOperation(op);
        }
      } else {
        // Single operation
        this._redoSingleOperation(operation);
      }

      // Move back to undo stack
      this.undoStack.push(operation);

      this.stats.redoCount++;

      // Update UI
      this._updateButtons();
      this._refreshViewer();

      // Show notification
      const opName = this._getOperationDisplayName(operation);
      Utils.notify(`Redid: ${opName}`, 'success');

      console.log('[HistoryManager] Redone:', operation.type);
      return true;

    } catch (error) {
      console.error('[HistoryManager] Redo error:', error);
      Utils.notify(`Redo failed: ${error.message}`, 'error');

      // Put operation back on redo stack
      this.redoStack.push(operation);
      return false;

    } finally {
      this.isExecuting = false;
    }
  }

  /**
   * Undo a single operation
   * @private
   */
  _undoSingleOperation(operation) {
    switch (operation.type) {
      case 'add_feature':
        this._undoAddFeature(operation);
        break;

      case 'delete_feature':
        this._undoDeleteFeature(operation);
        break;

      case 'edit_feature':
        this._undoEditFeature(operation);
        break;

      case 'add_sketch':
        this._undoAddSketch(operation);
        break;

      case 'edit_sketch':
        this._undoEditSketch(operation);
        break;

      case 'delete_sketch':
        this._undoDeleteSketch(operation);
        break;

      case 'add_sketch_entity':
        this._undoAddSketchEntity(operation);
        break;

      case 'delete_sketch_entity':
        this._undoDeleteSketchEntity(operation);
        break;

      case 'add_constraint':
        this._undoAddConstraint(operation);
        break;

      case 'add_dimension':
        this._undoAddDimension(operation);
        break;

      case 'delete_constraint':
        this._undoDeleteConstraint(operation);
        break;

      case 'create_primitive':
        this._undoCreatePrimitive(operation);
        break;

      case 'linear_pattern':
        this._undoLinearPattern(operation);
        break;

      case 'circular_pattern':
        this._undoCircularPattern(operation);
        break;

      case 'transform':
        this._undoTransform(operation);
        break;

      case 'boolean_operation':
        this._undoBooleanOperation(operation);
        break;

      case 'boolean_union':
      case 'boolean_cut':
      case 'boolean_intersect':
        this._undoSpecificBooleanOp(operation);
        break;

      default:
        console.warn('[HistoryManager] Unknown operation type for undo:', operation.type);
    }
  }

  /**
   * Redo a single operation
   * @private
   */
  _redoSingleOperation(operation) {
    switch (operation.type) {
      case 'add_feature':
        this._redoAddFeature(operation);
        break;

      case 'delete_feature':
        this._redoDeleteFeature(operation);
        break;

      case 'edit_feature':
        this._redoEditFeature(operation);
        break;

      case 'add_sketch':
        this._redoAddSketch(operation);
        break;

      case 'edit_sketch':
        this._redoEditSketch(operation);
        break;

      case 'delete_sketch':
        this._redoDeleteSketch(operation);
        break;

      case 'add_sketch_entity':
        this._redoAddSketchEntity(operation);
        break;

      case 'delete_sketch_entity':
        this._redoDeleteSketchEntity(operation);
        break;

      case 'add_constraint':
        this._redoAddConstraint(operation);
        break;

      case 'add_dimension':
        this._redoAddDimension(operation);
        break;

      case 'delete_constraint':
        this._redoDeleteConstraint(operation);
        break;

      case 'create_primitive':
        this._redoCreatePrimitive(operation);
        break;

      case 'linear_pattern':
        this._redoLinearPattern(operation);
        break;

      case 'circular_pattern':
        this._redoCircularPattern(operation);
        break;

      case 'transform':
        this._redoTransform(operation);
        break;

      case 'boolean_operation':
        this._redoBooleanOperation(operation);
        break;

      case 'boolean_union':
      case 'boolean_cut':
      case 'boolean_intersect':
        this._redoSpecificBooleanOp(operation);
        break;

      default:
        console.warn('[HistoryManager] Unknown operation type for redo:', operation.type);
    }
  }

  // ==================== Feature Operations ====================

  /**
   * Remove a mesh from the scene and cadObjects, disposing resources
   * @private
   */
  _removeMeshFromScene(featureId) {
    const mesh = this.cadViewer.cadObjects.get(featureId);
    if (mesh) {
      this.cadViewer.scene.remove(mesh);
      // Dispose geometry and materials to free GPU memory
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(m => m.dispose());
        } else {
          mesh.material.dispose();
        }
      }
      this.cadViewer.cadObjects.delete(featureId);
      console.log('[HistoryManager] Removed mesh for:', featureId);
    }
  }

  /**
   * Undo add feature
   */
  _undoAddFeature(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    // Remove feature from document
    doc.deleteFeature(operation.featureId);

    // Remove from parts array if this operation created a part
    if (operation.partId) {
      const partIndex = doc.parts.findIndex(p => p.id === operation.partId);
      if (partIndex !== -1) {
        doc.parts.splice(partIndex, 1);
        doc.hasUnsavedChanges = true;
      }
    }

    // Remove from scene with proper disposal
    this._removeMeshFromScene(operation.featureId);

    // Update feature tree
    this._updateFeatureTree();
  }

  /**
   * Redo add feature
   */
  _redoAddFeature(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    // Restore feature to document
    if (operation.feature) {
      doc.features.push(operation.feature);
    }

    // Restore part to document if this operation created a part
    if (operation.part) {
      doc.parts.push(operation.part);
      doc.hasUnsavedChanges = true;
    }

    // Recreate geometry if available
    if (operation.geometry) {
      this._restoreGeometry(operation.featureId, operation.geometry);
    }

    // Update feature tree
    this._updateFeatureTree();
  }

  /**
   * Undo delete feature
   */
  _undoDeleteFeature(operation) {
    // Restore deleted feature (same as redo add)
    this._redoAddFeature({
      featureId: operation.featureId,
      partId: operation.partId,
      feature: operation.deletedFeature,
      part: operation.deletedPart,
      geometry: operation.deletedGeometry
    });
  }

  /**
   * Redo delete feature
   */
  _redoDeleteFeature(operation) {
    // Delete feature (same as undo add)
    this._undoAddFeature({
      featureId: operation.featureId,
      partId: operation.partId
    });
  }

  /**
   * Undo edit feature
   */
  _undoEditFeature(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    const feature = doc.getFeature(operation.featureId);
    if (feature) {
      // Restore old state
      Object.assign(feature, operation.oldState);

      // Update geometry if needed
      if (operation.oldGeometry) {
        this._restoreGeometry(operation.featureId, operation.oldGeometry);
      }

      this._updateFeatureTree();
    }
  }

  /**
   * Redo edit feature
   */
  _redoEditFeature(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    const feature = doc.getFeature(operation.featureId);
    if (feature) {
      // Apply new state
      Object.assign(feature, operation.newState);

      // Update geometry if needed
      if (operation.newGeometry) {
        this._restoreGeometry(operation.featureId, operation.newGeometry);
      }

      this._updateFeatureTree();
    }
  }

  // ==================== Sketch Operations ====================

  /**
   * Find a sketch by ID - checks both doc.features and doc.sketches
   * @private
   */
  _findSketch(sketchId) {
    const doc = this.cadViewer.document;
    if (!doc) return null;
    // Sketches are stored as features with type 'sketch'
    return doc.features.find(f => f.id === sketchId && f.type === 'sketch')
      || doc.sketches?.find(s => s.id === sketchId)
      || null;
  }

  /**
   * Undo add sketch
   */
  _undoAddSketch(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    // Remove sketch from features array (where finishSketch stores it)
    const featureIndex = doc.features.findIndex(f => f.id === operation.sketchId);
    if (featureIndex !== -1) {
      doc.features.splice(featureIndex, 1);
    }

    // Also remove from sketches array if present
    const sketchIndex = doc.sketches?.findIndex(s => s.id === operation.sketchId);
    if (sketchIndex !== -1 && sketchIndex !== undefined) {
      doc.sketches.splice(sketchIndex, 1);
    }

    // Remove 3D sketch representation from scene (primary and construction meshes)
    this._removeMeshFromScene(operation.sketchId);
    this._removeMeshFromScene(operation.sketchId + '_construction');

    // Close sketch if it's active
    if (this.cadViewer.sketchEngine?.activeSketch?.id === operation.sketchId) {
      if (this.cadViewer.sketchEngine.cancelSketch) {
        this.cadViewer.sketchEngine.cancelSketch();
      }
    }

    this._updateFeatureTree();
  }

  /**
   * Redo add sketch
   */
  _redoAddSketch(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    // Restore sketch feature to document
    if (operation.sketch) {
      doc.features.push(operation.sketch);
    }

    // Recreate 3D sketch line segments in the scene so the geometry is visible again.
    // The sketch data lives in operation.sketch.parameters (entities array).
    const sketch = operation.sketch;
    if (sketch && this.cadViewer.sketchEngine) {
      // Build the object create3DSketch expects: { id, name, entities }
      const sketchForRender = {
        id: sketch.id,
        name: sketch.name || sketch.parameters?.name || 'Sketch',
        entities: sketch.parameters?.entities || []
      };

      // Remove any stale mesh for this id before recreating
      this._removeMeshFromScene(sketch.id);
      this._removeMeshFromScene(sketch.id + '_construction');

      this.cadViewer.sketchEngine.create3DSketch(sketchForRender);
      console.log('[HistoryManager] Redo: recreated 3D sketch for', sketch.id);
    }

    this._updateFeatureTree();
  }

  /**
   * Undo edit sketch
   */
  _undoEditSketch(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    // Find sketch in features array
    const sketch = doc.features.find(f => f.id === operation.sketchId);
    if (sketch && sketch.parameters) {
      // Restore old entities and constraints
      sketch.parameters.entities = JSON.parse(JSON.stringify(operation.oldEntities));
      if (operation.oldConstraints) {
        sketch.parameters.constraints = JSON.parse(JSON.stringify(operation.oldConstraints));
      }

      // Update sketch view if active
      if (this.cadViewer.sketchEngine?.activeSketch?.id === operation.sketchId) {
        this.cadViewer.sketchEngine.entities = [...sketch.parameters.entities];
        this.cadViewer.sketchEngine.render();
      }
    }
  }

  /**
   * Redo edit sketch
   */
  _redoEditSketch(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    // Find sketch in features array
    const sketch = doc.features.find(f => f.id === operation.sketchId);
    if (sketch && sketch.parameters) {
      // Apply new entities and constraints
      sketch.parameters.entities = JSON.parse(JSON.stringify(operation.newEntities));
      if (operation.newConstraints) {
        sketch.parameters.constraints = JSON.parse(JSON.stringify(operation.newConstraints));
      }

      // Update sketch view if active
      if (this.cadViewer.sketchEngine?.activeSketch?.id === operation.sketchId) {
        this.cadViewer.sketchEngine.entities = [...sketch.parameters.entities];
        this.cadViewer.sketchEngine.render();
      }
    }
  }

  /**
   * Undo delete sketch
   */
  _undoDeleteSketch(operation) {
    this._redoAddSketch({
      sketchId: operation.sketchId,
      sketch: operation.deletedSketch
    });
  }

  /**
   * Redo delete sketch
   */
  _redoDeleteSketch(operation) {
    this._undoAddSketch({
      sketchId: operation.sketchId
    });
  }

  // ==================== Sketch Entity Operations ====================

  /**
   * Get the sketch entities array by sketchId.
   * Checks active sketch engine first, then document features.
   * @private
   */
  _getSketchEntities(sketchId) {
    // If sketch is currently active, use the live entities
    if (this.cadViewer.sketchEngine?.activeSketch?.id === sketchId) {
      return { source: 'engine', entities: this.cadViewer.sketchEngine.entities };
    }
    // Otherwise find sketch in document features
    const doc = this.cadViewer.document;
    if (doc) {
      const feature = doc.features.find(f => f.id === sketchId && f.type === 'sketch');
      if (feature && feature.parameters) {
        return { source: 'document', entities: feature.parameters.entities || [] };
      }
    }
    return null;
  }

  /**
   * Undo add sketch entity
   */
  _undoAddSketchEntity(operation) {
    const sketchRef = this._getSketchEntities(operation.sketchId);
    if (!sketchRef) {
      console.warn('[HistoryManager] Cannot find sketch for entity undo:', operation.sketchId);
      return;
    }

    const index = sketchRef.entities.findIndex(e => e.id === operation.entityId);
    if (index !== -1) {
      sketchRef.entities.splice(index, 1);
      // Render if sketch is active
      if (sketchRef.source === 'engine' && this.cadViewer.sketchEngine) {
        this.cadViewer.sketchEngine.render();
      }
    }
  }

  /**
   * Redo add sketch entity
   */
  _redoAddSketchEntity(operation) {
    const sketchRef = this._getSketchEntities(operation.sketchId);
    if (!sketchRef) {
      console.warn('[HistoryManager] Cannot find sketch for entity redo:', operation.sketchId);
      return;
    }

    sketchRef.entities.push(operation.entity);
    // Render if sketch is active
    if (sketchRef.source === 'engine' && this.cadViewer.sketchEngine) {
      this.cadViewer.sketchEngine.render();
    }
  }

  /**
   * Undo delete sketch entity
   */
  _undoDeleteSketchEntity(operation) {
    this._redoAddSketchEntity({
      sketchId: operation.sketchId,
      entityId: operation.entityId,
      entity: operation.deletedEntity
    });
  }

  /**
   * Redo delete sketch entity
   */
  _redoDeleteSketchEntity(operation) {
    this._undoAddSketchEntity({
      sketchId: operation.sketchId,
      entityId: operation.entityId
    });
  }

  // ==================== Constraint Operations ====================

  /**
   * Get the sketch constraints array by sketchId.
   * @private
   */
  _getSketchConstraints(sketchId) {
    // If sketch is currently active
    if (this.cadViewer.sketchEngine?.activeSketch?.id === sketchId) {
      const activeSketch = this.cadViewer.sketchEngine.activeSketch;
      if (!activeSketch.constraints) activeSketch.constraints = [];
      return { source: 'engine', constraints: activeSketch.constraints };
    }
    // Find in document features
    const doc = this.cadViewer.document;
    if (doc) {
      const feature = doc.features.find(f => f.id === sketchId && f.type === 'sketch');
      if (feature && feature.parameters) {
        if (!feature.parameters.constraints) feature.parameters.constraints = [];
        return { source: 'document', constraints: feature.parameters.constraints };
      }
    }
    return null;
  }

  /**
   * Undo add constraint
   */
  _undoAddConstraint(operation) {
    const sketchRef = this._getSketchConstraints(operation.sketchId);
    if (!sketchRef) {
      console.warn('[HistoryManager] Cannot find sketch for constraint undo:', operation.sketchId);
      return;
    }

    const index = sketchRef.constraints.findIndex(c => c.id === operation.constraintId);
    if (index !== -1) {
      sketchRef.constraints.splice(index, 1);
      if (sketchRef.source === 'engine' && this.cadViewer.sketchEngine) {
        this.cadViewer.sketchEngine.render();
      }
    }
  }

  /**
   * Redo add constraint
   */
  _redoAddConstraint(operation) {
    const sketchRef = this._getSketchConstraints(operation.sketchId);
    if (!sketchRef) {
      console.warn('[HistoryManager] Cannot find sketch for constraint redo:', operation.sketchId);
      return;
    }

    sketchRef.constraints.push(operation.constraint);
    if (sketchRef.source === 'engine' && this.cadViewer.sketchEngine) {
      this.cadViewer.sketchEngine.render();
    }
  }

  /**
   * Undo delete constraint
   */
  _undoDeleteConstraint(operation) {
    this._redoAddConstraint({
      sketchId: operation.sketchId,
      constraintId: operation.constraintId,
      constraint: operation.deletedConstraint
    });
  }

  /**
   * Redo delete constraint
   */
  _redoDeleteConstraint(operation) {
    this._undoAddConstraint({
      sketchId: operation.sketchId,
      constraintId: operation.constraintId
    });
  }

  // ==================== Dimension Operations ====================

  /**
   * Undo add dimension
   */
  _undoAddDimension(operation) {
    // Find the sketch and remove the dimension
    const doc = this.cadViewer.document;
    if (!doc) return;

    const sketch = doc.features.find(f => f.id === operation.sketchId);
    if (sketch && sketch.parameters && sketch.parameters.dimensions) {
      const index = sketch.parameters.dimensions.findIndex(d => d.id === operation.dimensionId);
      if (index !== -1) {
        sketch.parameters.dimensions.splice(index, 1);
      }
    }

    // Also check active sketch in sketch engine
    if (this.cadViewer.sketchEngine?.activeSketch?.id === operation.sketchId) {
      const activeSketch = this.cadViewer.sketchEngine.activeSketch;
      if (activeSketch.dimensions) {
        const index = activeSketch.dimensions.findIndex(d => d.id === operation.dimensionId);
        if (index !== -1) {
          activeSketch.dimensions.splice(index, 1);
        }
      }
      this.cadViewer.sketchEngine.render();
    }
  }

  /**
   * Redo add dimension
   */
  _redoAddDimension(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    const sketch = doc.features.find(f => f.id === operation.sketchId);
    if (sketch && sketch.parameters) {
      if (!sketch.parameters.dimensions) sketch.parameters.dimensions = [];
      sketch.parameters.dimensions.push(operation.dimension);
    }

    // Also update active sketch
    if (this.cadViewer.sketchEngine?.activeSketch?.id === operation.sketchId) {
      const activeSketch = this.cadViewer.sketchEngine.activeSketch;
      if (!activeSketch.dimensions) activeSketch.dimensions = [];
      activeSketch.dimensions.push(operation.dimension);
      this.cadViewer.sketchEngine.render();
    }
  }

  // ==================== Pattern Operations ====================

  /**
   * Undo linear pattern
   */
  _undoLinearPattern(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    // Remove the feature document entry
    doc.deleteFeature(operation.featureId);

    // Remove all pattern instance meshes from scene
    if (operation.patternInstanceIds) {
      for (const instanceId of operation.patternInstanceIds) {
        this._removeMeshFromScene(instanceId);
      }
    }

    this._updateFeatureTree();
  }

  /**
   * Redo linear pattern
   */
  _redoLinearPattern(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    // Restore feature to document
    if (operation.feature) {
      doc.features.push(operation.feature);
    }

    // Recreate pattern instance meshes by re-running the same clone logic used
    // during the original operation.  The saved patternInstanceIds array mirrors
    // the order used when the pattern was first created: index 0 is the primary
    // feature mesh (already in the scene from its own feature record) and indices
    // 1..n-1 are the additional clones positioned along the X-axis.
    const instanceIds = operation.patternInstanceIds;
    const feature = operation.feature;
    if (instanceIds && instanceIds.length > 1 && feature) {
      const targetFeatureId = feature.parameters?.targetFeature;
      const sourceMesh = targetFeatureId
        ? this.cadViewer.cadObjects.get(targetFeatureId)
        : null;

      if (sourceMesh) {
        const spacing = feature.parameters?.spacing ?? 1;
        // instanceIds[0] is the pattern feature itself (no mesh clone needed there)
        for (let i = 1; i < instanceIds.length; i++) {
          const instanceId = instanceIds[i];
          // Remove any stale mesh before re-adding
          this._removeMeshFromScene(instanceId);

          const clonedMesh = sourceMesh.clone();
          clonedMesh.position.x = sourceMesh.position.x + (i * spacing);
          clonedMesh.userData = {
            isCADFeature: true,
            featureId: instanceId,
            featureName: (feature.name || feature.parameters?.name || 'Pattern') + '_' + i
          };
          this.cadViewer.scene.add(clonedMesh);
          this.cadViewer.cadObjects.set(instanceId, clonedMesh);
        }
        console.log('[HistoryManager] Redo: recreated', instanceIds.length - 1, 'linear pattern instance(s)');
      } else {
        console.warn('[HistoryManager] Linear pattern redo: source mesh not found for', targetFeatureId);
      }
    }

    this._updateFeatureTree();
  }

  /**
   * Undo circular pattern
   */
  _undoCircularPattern(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    // Remove the feature document entry
    doc.deleteFeature(operation.featureId);

    // Remove all pattern instance meshes from scene
    if (operation.patternInstanceIds) {
      for (const instanceId of operation.patternInstanceIds) {
        this._removeMeshFromScene(instanceId);
      }
    }

    this._updateFeatureTree();
  }

  /**
   * Redo circular pattern
   */
  _redoCircularPattern(operation) {
    const doc = this.cadViewer.document;
    if (!doc) return;

    // Restore feature to document
    if (operation.feature) {
      doc.features.push(operation.feature);
    }

    // Recreate circular pattern instance meshes using the same placement math
    // as the original performCircularPattern call.
    const instanceIds = operation.patternInstanceIds;
    const feature = operation.feature;
    if (instanceIds && instanceIds.length > 1 && feature) {
      const targetFeatureId = feature.parameters?.targetFeature;
      const sourceMesh = targetFeatureId
        ? this.cadViewer.cadObjects.get(targetFeatureId)
        : null;

      if (sourceMesh) {
        const n = feature.parameters?.count ?? instanceIds.length;
        const angleStep = (2 * Math.PI) / n;
        const radius = 2; // Same default radius used during creation

        for (let i = 1; i < instanceIds.length; i++) {
          const instanceId = instanceIds[i];
          // Remove any stale mesh before re-adding
          this._removeMeshFromScene(instanceId);

          const clonedMesh = sourceMesh.clone();
          const angle = i * angleStep;
          clonedMesh.position.x = Math.cos(angle) * radius;
          clonedMesh.position.z = Math.sin(angle) * radius;
          clonedMesh.userData = {
            isCADFeature: true,
            featureId: instanceId,
            featureName: (feature.name || feature.parameters?.name || 'Pattern') + '_' + i
          };
          this.cadViewer.scene.add(clonedMesh);
          this.cadViewer.cadObjects.set(instanceId, clonedMesh);
        }
        console.log('[HistoryManager] Redo: recreated', instanceIds.length - 1, 'circular pattern instance(s)');
      } else {
        console.warn('[HistoryManager] Circular pattern redo: source mesh not found for', targetFeatureId);
      }
    }

    this._updateFeatureTree();
  }

  // ==================== Primitive Operations ====================

  /**
   * Undo create primitive
   */
  _undoCreatePrimitive(operation) {
    this._undoAddFeature({
      featureId: operation.featureId
    });
  }

  /**
   * Redo create primitive
   */
  _redoCreatePrimitive(operation) {
    this._redoAddFeature({
      featureId: operation.featureId,
      feature: operation.feature,
      geometry: operation.geometry
    });
  }

  // ==================== Transform Operations ====================

  /**
   * Undo transform
   */
  _undoTransform(operation) {
    const mesh = this.cadViewer.cadObjects.get(operation.featureId);
    if (mesh && operation.oldTransform) {
      mesh.position.copy(operation.oldTransform.position);
      mesh.rotation.copy(operation.oldTransform.rotation);
      mesh.scale.copy(operation.oldTransform.scale);
    }
  }

  /**
   * Redo transform
   */
  _redoTransform(operation) {
    const mesh = this.cadViewer.cadObjects.get(operation.featureId);
    if (mesh && operation.newTransform) {
      mesh.position.copy(operation.newTransform.position);
      mesh.rotation.copy(operation.newTransform.rotation);
      mesh.scale.copy(operation.newTransform.scale);
    }
  }

  // ==================== Boolean Operations ====================

  /**
   * Undo boolean operation
   */
  _undoBooleanOperation(operation) {
    // Remove result feature
    this._undoAddFeature({
      featureId: operation.resultFeatureId
    });

    // Restore operand features
    if (operation.operandFeatures) {
      for (const feature of operation.operandFeatures) {
        this._redoAddFeature({
          featureId: feature.id,
          feature: feature,
          geometry: feature.geometry
        });
      }
    }
  }

  /**
   * Redo boolean operation
   */
  _redoBooleanOperation(operation) {
    // Remove operand features
    if (operation.operandFeatures) {
      for (const feature of operation.operandFeatures) {
        this._undoAddFeature({
          featureId: feature.id
        });
      }
    }

    // Restore result feature
    this._redoAddFeature({
      featureId: operation.resultFeatureId,
      feature: operation.resultFeature,
      geometry: operation.resultGeometry
    });
  }

  /**
   * Undo boolean union/cut/intersect (the new specific boolean operations)
   * Removes the result mesh and makes hidden source meshes visible again
   * @private
   */
  _undoSpecificBooleanOp(operation) {
    // Remove the result mesh
    this._removeMeshFromScene(operation.resultId);

    // Restore visibility of hidden source meshes
    if (operation.hiddenIds) {
      for (const id of operation.hiddenIds) {
        const mesh = this.cadViewer.cadObjects.get(id);
        if (mesh) {
          mesh.visible = true;
        }
      }
    }
  }

  /**
   * Redo boolean union/cut/intersect
   * Re-hides source meshes and restores the result mesh
   * @private
   */
  _redoSpecificBooleanOp(operation) {
    // Restore the result mesh
    if (operation.geometry) {
      this._restoreGeometry(operation.resultId, operation.geometry);
    }

    // Hide source meshes again
    if (operation.hiddenIds) {
      for (const id of operation.hiddenIds) {
        const mesh = this.cadViewer.cadObjects.get(id);
        if (mesh) {
          mesh.visible = false;
        }
      }
    }
  }

  // ==================== Helper Methods ====================

  /**
   * Check if undo is possible
   */
  canUndo() {
    return this.undoStack.length > 0;
  }

  /**
   * Check if redo is possible
   */
  canRedo() {
    return this.redoStack.length > 0;
  }

  /**
   * Clear all history
   */
  clear() {
    // Cleanup operations
    for (const op of this.undoStack) {
      this._cleanupOperation(op);
    }
    for (const op of this.redoStack) {
      this._cleanupOperation(op);
    }

    this.undoStack = [];
    this.redoStack = [];

    this._updateButtons();

    console.log('[HistoryManager] History cleared');
  }

  /**
   * Start grouping operations (for multi-step operations)
   */
  beginGroup(name = 'Group') {
    if (this.grouping) {
      console.warn('[HistoryManager] Already grouping operations');
      return;
    }

    this.grouping = {
      type: 'group',
      name: name,
      operations: []
    };

    console.log('[HistoryManager] Started operation group:', name);
  }

  /**
   * End grouping and record as single operation
   */
  endGroup() {
    if (!this.grouping) {
      console.warn('[HistoryManager] No active group');
      return;
    }

    if (this.grouping.operations.length > 0) {
      // Record group as single operation
      this.recordOperation(this.grouping);
    }

    this.grouping = null;
    console.log('[HistoryManager] Ended operation group');
  }

  /**
   * Cancel current grouping without recording
   */
  cancelGroup() {
    if (this.grouping) {
      console.log('[HistoryManager] Cancelled operation group');
      this.grouping = null;
    }
  }

  /**
   * Enable/disable history tracking
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    console.log('[HistoryManager] History tracking:', enabled ? 'enabled' : 'disabled');
  }

  /**
   * Get operation display name for UI
   * @private
   */
  _getOperationDisplayName(operation) {
    if (operation.name) {
      return operation.name;
    }

    if (operation.operations) {
      return `${operation.name || 'Group'} (${operation.operations.length} operations)`;
    }

    // Default names based on operation type
    const names = {
      'add_feature': 'Add Feature',
      'delete_feature': 'Delete Feature',
      'edit_feature': 'Edit Feature',
      'add_sketch': 'Add Sketch',
      'edit_sketch': 'Edit Sketch',
      'delete_sketch': 'Delete Sketch',
      'add_sketch_entity': 'Add Entity',
      'delete_sketch_entity': 'Delete Entity',
      'add_constraint': 'Add Constraint',
      'delete_constraint': 'Delete Constraint',
      'add_dimension': 'Add Dimension',
      'create_primitive': 'Create Primitive',
      'linear_pattern': 'Linear Pattern',
      'circular_pattern': 'Circular Pattern',
      'transform': 'Transform',
      'boolean_operation': 'Boolean Operation',
      'boolean_union': 'Boolean Union',
      'boolean_cut': 'Boolean Subtract',
      'boolean_intersect': 'Boolean Intersect'
    };

    return names[operation.type] || operation.type;
  }

  /**
   * Update undo/redo button states
   * @private
   */
  _updateButtons() {
    const undoBtn = document.getElementById('btnUndo');
    const redoBtn = document.getElementById('btnRedo');

    if (undoBtn) {
      undoBtn.disabled = !this.canUndo();
      undoBtn.classList.toggle('disabled', !this.canUndo());

      // Update tooltip with operation name
      if (this.canUndo()) {
        const lastOp = this.undoStack[this.undoStack.length - 1];
        undoBtn.title = `Undo: ${this._getOperationDisplayName(lastOp)} (Ctrl+Z)`;
      } else {
        undoBtn.title = 'Undo (Ctrl+Z)';
      }
    }

    if (redoBtn) {
      redoBtn.disabled = !this.canRedo();
      redoBtn.classList.toggle('disabled', !this.canRedo());

      // Update tooltip with operation name
      if (this.canRedo()) {
        const nextOp = this.redoStack[this.redoStack.length - 1];
        redoBtn.title = `Redo: ${this._getOperationDisplayName(nextOp)} (Ctrl+Y)`;
      } else {
        redoBtn.title = 'Redo (Ctrl+Y / Ctrl+Shift+Z)';
      }
    }
  }

  /**
   * Refresh viewer after undo/redo
   * @private
   */
  _refreshViewer() {
    // Update feature tree
    this._updateFeatureTree();

    // Render Three.js scene - try multiple render paths
    const engine = this.cadViewer.engine;
    if (engine) {
      // Try standard render method
      if (typeof engine.render === 'function') {
        engine.render();
      }
      // Try renderer directly
      if (engine.renderer && engine.scene && engine.camera) {
        engine.renderer.render(engine.scene, engine.camera);
      }
    }

    // Also try global renderer if available
    if (typeof window !== 'undefined' && window.cadViewer?.engine?.renderer) {
      const e = window.cadViewer.engine;
      if (e.renderer && e.scene && e.camera) {
        e.renderer.render(e.scene, e.camera);
      }
    }

    // Update sketch view if active
    if (this.cadViewer.sketchEngine?.activeSketch) {
      this.cadViewer.sketchEngine.render();
    }
  }

  /**
   * Update feature tree UI
   * @private
   */
  _updateFeatureTree() {
    if (this.cadViewer.updateFeatureTree) {
      this.cadViewer.updateFeatureTree();
    }
  }

  /**
   * Restore geometry from saved state
   * @private
   */
  _restoreGeometry(featureId, geometryData) {
    // Remove old mesh
    const oldMesh = this.cadViewer.cadObjects.get(featureId);
    if (oldMesh) {
      this.cadViewer.scene.remove(oldMesh);
    }

    // Create new mesh from geometry data
    if (geometryData.type === 'BufferGeometry') {
      const geometry = new THREE.BufferGeometry();

      if (geometryData.attributes.position) {
        geometry.setAttribute('position',
          new THREE.Float32BufferAttribute(geometryData.attributes.position, 3));
      }
      if (geometryData.attributes.normal) {
        geometry.setAttribute('normal',
          new THREE.Float32BufferAttribute(geometryData.attributes.normal, 3));
      }
      if (geometryData.index) {
        geometry.setIndex(geometryData.index);
      }

      const material = new THREE.MeshStandardMaterial({
        color: geometryData.color || 0x3498db,
        metalness: 0.3,
        roughness: 0.7
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.userData.featureId = featureId;

      if (geometryData.transform) {
        mesh.position.copy(geometryData.transform.position);
        mesh.rotation.copy(geometryData.transform.rotation);
        mesh.scale.copy(geometryData.transform.scale);
      }

      this.cadViewer.scene.add(mesh);
      this.cadViewer.cadObjects.set(featureId, mesh);
    }
  }

  /**
   * Serialize geometry for storage
   * @private
   */
  _serializeGeometry(mesh) {
    if (!mesh || !mesh.geometry) {
      return null;
    }

    const data = {
      type: 'BufferGeometry',
      attributes: {},
      color: mesh.material?.color?.getHex(),
      transform: {
        position: mesh.position.clone(),
        rotation: mesh.rotation.clone(),
        scale: mesh.scale.clone()
      }
    };

    // Store vertex positions
    if (mesh.geometry.attributes.position) {
      data.attributes.position = Array.from(mesh.geometry.attributes.position.array);
    }

    // Store normals
    if (mesh.geometry.attributes.normal) {
      data.attributes.normal = Array.from(mesh.geometry.attributes.normal.array);
    }

    // Store indices
    if (mesh.geometry.index) {
      data.index = Array.from(mesh.geometry.index.array);
    }

    return data;
  }

  /**
   * Cleanup operation data to free memory
   * @private
   */
  _cleanupOperation(operation) {
    // Clean up any large data structures
    if (operation.geometry) {
      operation.geometry = null;
    }
    if (operation.oldGeometry) {
      operation.oldGeometry = null;
    }
    if (operation.newGeometry) {
      operation.newGeometry = null;
    }
  }

  /**
   * Get history statistics
   */
  getStats() {
    return {
      ...this.stats,
      undoStackSize: this.undoStack.length,
      redoStackSize: this.redoStack.length,
      canUndo: this.canUndo(),
      canRedo: this.canRedo()
    };
  }

  /**
   * Export history for debugging
   */
  exportHistory() {
    return {
      undoStack: this.undoStack.map(op => ({
        type: op.type,
        name: this._getOperationDisplayName(op),
        timestamp: op.timestamp
      })),
      redoStack: this.redoStack.map(op => ({
        type: op.type,
        name: this._getOperationDisplayName(op),
        timestamp: op.timestamp
      })),
      stats: this.getStats()
    };
  }

  /**
   * Print history status to console (for debugging)
   */
  printStatus() {
    console.log('[HistoryManager] Status:');
    console.log('  Undo stack:', this.undoStack.length, 'operations');
    console.log('  Redo stack:', this.redoStack.length, 'operations');
    console.log('  Can undo:', this.canUndo());
    console.log('  Can redo:', this.canRedo());
    console.log('  Total operations recorded:', this.stats.totalOperations);
    console.log('  Undo count:', this.stats.undoCount);
    console.log('  Redo count:', this.stats.redoCount);

    if (this.undoStack.length > 0) {
      console.log('  Recent operations:');
      this.undoStack.slice(-5).forEach((op, i) => {
        console.log(`    ${i + 1}. ${this._getOperationDisplayName(op)}`);
      });
    }
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HistoryManager;
}

// Make available globally for debugging
if (typeof window !== 'undefined') {
  window.HistoryManager = HistoryManager;
}
